import React, { useEffect, useState, useCallback } from 'react';
import { Plus, Edit, Trash, X, Check, Package, Briefcase, Zap } from 'lucide-react';
import api from '../utils/api';

const PacksList = () => {
    const [packs, setPacks] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        maxActiveOffers: 1,
        offerDurationDays: 30,
        price: 0,
        description: '',
        features: {
            cvtheque: false,
            missions: false
        }
    });
    const [selectedPackId, setSelectedPackId] = useState(null);

    const fetchPacks = useCallback(async () => {
        try {
            setIsLoading(true);
            const res = await api.get('/admin/packs');
            setPacks(res.data);
        } catch (error) {
            console.error('Error fetching packs:', error);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        document.title = 'Packs - Admin Tekhdem';
        fetchPacks();
    }, [fetchPacks]);

    const resetForm = () => {
        setFormData({
            name: '',
            maxActiveOffers: 1,
            offerDurationDays: 30,
            price: 0,
            description: '',
            features: {
                cvtheque: false,
                missions: false
            }
        });
        setIsEditing(false);
        setSelectedPackId(null);
    };

    const handleEdit = (pack) => {
        setFormData({
            name: pack.name,
            maxActiveOffers: pack.maxActiveOffers,
            offerDurationDays: pack.offerDurationDays,
            price: pack.price,
            description: pack.description || '',
            features: {
                cvtheque: pack.features?.cvtheque || false,
                missions: pack.features?.missions || false
            }
        });
        setSelectedPackId(pack._id);
        setIsEditing(true);
        setShowModal(true);
    };

    const handleDelete = async (id) => {
        if (window.confirm('Êtes-vous sûr de vouloir supprimer ce pack ?')) {
            try {
                await api.delete(`/admin/packs/${id}`);
                fetchPacks();
            } catch (error) {
                console.error('Error deleting pack:', error);
            }
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (isEditing) {
                await api.put(`/admin/packs/${selectedPackId}`, formData);
            } else {
                await api.post('/admin/packs', formData);
            }
            setShowModal(false);
            resetForm();
            fetchPacks();
        } catch (error) {
            console.error('Error saving pack:', error);
            alert('Erreur lors de l\'enregistrement');
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (name.startsWith('features.')) {
            const featureName = name.split('.')[1];
            setFormData(prev => ({
                ...prev,
                features: {
                    ...prev.features,
                    [featureName]: checked
                }
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: type === 'checkbox' ? checked : value
            }));
        }
    };

    return (
        <div className="space-y-6 animate-in fade-in duration-500">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Packs & Abonnements</h1>
                    <p className="text-slate-500 dark:text-slate-400 text-sm">Gérez les offres commerciales pour les recruteurs</p>
                </div>
                <button
                    onClick={() => { resetForm(); setShowModal(true); }}
                    className="flex items-center gap-2 px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-lg transition-colors shadow-sm"
                >
                    <Plus className="w-5 h-5" />
                    Nouveau Pack
                </button>
            </div>

            {isLoading ? (
                <div className="flex justify-center py-12">
                    <div className="w-8 h-8 border-4 border-slate-900 border-t-transparent rounded-full animate-spin"></div>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {packs.map((pack) => (
                        <div key={pack._id} className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-6 shadow-sm hover:shadow-md transition-all relative group">
                            <div className="flex justify-between items-start mb-4">
                                <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-blue-600 dark:text-blue-400">
                                    <Package className="w-6 h-6" />
                                </div>
                                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <button onClick={() => handleEdit(pack)} className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg">
                                        <Edit className="w-4 h-4" />
                                    </button>
                                    <button onClick={() => handleDelete(pack._id)} className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg">
                                        <Trash className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>

                            <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">{pack.name}</h3>
                            <div className="text-2xl font-bold text-slate-900 dark:text-white mb-4">
                                {pack.price} <span className="text-sm font-normal text-slate-500">DZD</span>
                            </div>

                            <div className="space-y-3 mb-6">
                                <div className="flex items-center gap-3 text-sm text-slate-600 dark:text-slate-300">
                                    <Briefcase className="w-4 h-4 text-slate-400" />
                                    <span>{pack.maxActiveOffers} offres actives</span>
                                </div>
                                <div className="flex items-center gap-3 text-sm text-slate-600 dark:text-slate-300">
                                    <Zap className="w-4 h-4 text-slate-400" />
                                    <span>Durée validité: {pack.offerDurationDays} jours</span>
                                </div>
                            </div>

                            <div className="flex gap-2 text-xs">
                                {pack.features?.cvtheque && (
                                    <span className="px-2 py-1 bg-green-100 text-green-700 rounded-md font-medium">CVthèque</span>
                                )}
                                {pack.features?.missions && (
                                    <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-md font-medium">Missions</span>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white dark:bg-slate-900 rounded-2xl w-full max-w-lg p-6 shadow-2xl animate-in fade-in zoom-in-95 duration-200">
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="text-xl font-bold text-slate-900 dark:text-white">
                                {isEditing ? 'Modifier le Pack' : 'Nouveau Pack'}
                            </h3>
                            <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-slate-600">
                                <X className="w-6 h-6" />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Nom du Pack</label>
                                <input
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    required
                                    className="w-full px-4 py-2 border border-slate-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-slate-900 dark:focus:ring-white bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
                                    placeholder="Ex: Start, Pro, Business"
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Prix (DZD)</label>
                                    <input
                                        type="number"
                                        name="price"
                                        value={formData.price}
                                        onChange={handleChange}
                                        className="w-full px-4 py-2 border border-slate-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-slate-900 dark:focus:ring-white bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Durée Validité (Jours)</label>
                                    <input
                                        type="number"
                                        name="offerDurationDays"
                                        value={formData.offerDurationDays}
                                        onChange={handleChange}
                                        className="w-full px-4 py-2 border border-slate-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-slate-900 dark:focus:ring-white bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Max. Offres Actives</label>
                                <input
                                    type="number"
                                    name="maxActiveOffers"
                                    value={formData.maxActiveOffers}
                                    onChange={handleChange}
                                    className="w-full px-4 py-2 border border-slate-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-slate-900 dark:focus:ring-white bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
                                />
                            </div>

                            <div className="space-y-3 pt-2">
                                <label className="flex items-center gap-3 p-3 border border-slate-200 dark:border-slate-700 rounded-lg cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800">
                                    <input
                                        type="checkbox"
                                        name="features.cvtheque"
                                        checked={formData.features.cvtheque}
                                        onChange={handleChange}
                                        className="w-5 h-5 rounded text-slate-900 focus:ring-slate-900 border-gray-300"
                                    />
                                    <span className="font-medium text-slate-700 dark:text-slate-300">Accès CVthèque</span>
                                </label>
                                <label className="flex items-center gap-3 p-3 border border-slate-200 dark:border-slate-700 rounded-lg cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800">
                                    <input
                                        type="checkbox"
                                        name="features.missions"
                                        checked={formData.features.missions}
                                        onChange={handleChange}
                                        className="w-5 h-5 rounded text-slate-900 focus:ring-slate-900 border-gray-300"
                                    />
                                    <span className="font-medium text-slate-700 dark:text-slate-300">Publication Missions</span>
                                </label>
                            </div>

                            <div className="flex gap-3 pt-4">
                                <button
                                    type="button"
                                    onClick={() => setShowModal(false)}
                                    className="flex-1 px-4 py-2 border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 font-medium text-slate-700 dark:text-slate-300"
                                >
                                    Annuler
                                </button>
                                <button
                                    type="submit"
                                    className="flex-1 px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-lg font-medium"
                                >
                                    {isEditing ? 'Mettre à jour' : 'Créer le Pack'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PacksList;
